import React, { Suspense, useEffect, useState } from "react";
import { PaymentElement, LinkAuthenticationElement } from "@stripe/react-stripe-js";
import ProductCardsSkeleton from "./skeleton/ProductCardsSkeleton";
import ProductSummary from "./ProductSummary";
import AddressForm from "./AddressForm";
import TermsCheckbox from "./TermsCheckbox";
import ProductDisplay from "./ProductDisplay";
import OrderBump from "./OrderBump";
import HeadlineBox from "./HeadlineBox";
import useCustomerStore from "../store/customer-store";
import useStripeService from "../hooks/useStripeService";
import { sendGTMEvent } from "react-google-gtm";
import { getTranslation } from "../utils/localization";

interface PaymentFormProps {
  data: any;
  products: any[];
  bumpProducts: any[];
  bumpLast?: boolean;
  clientSecret: string;
  paymentIntentId: string;
  skuDetailsMap: any;
  finalPrice: number;
  productDisplayType: string;
  termsAccepted: boolean;
  setTermsAccepted: (accepted: boolean) => void;
  primaryButton: string;
  selectedProduct: any;
  selectedProductIndex: number;
  handleProductChange: (index: number) => void;
  handleBumpChange: (index: number, addBump: boolean) => void;
  skuPriceMap: any;
  theme?: string;
}

const PaymentForm: React.FC<PaymentFormProps> = ({
  data,
  products,
  productDisplayType,
  bumpProducts,
  bumpLast,
  clientSecret,
  paymentIntentId,
  skuDetailsMap,
  finalPrice,
  termsAccepted,
  setTermsAccepted,
  primaryButton,
  selectedProduct,
  selectedProductIndex,
  handleProductChange,
  handleBumpChange,
  skuPriceMap,
  theme,
}) => {
  // Use our custom Stripe service hook instead of directly accessing Stripe elements
  const { elements, handlePaymentSubmission } = useStripeService();
  const customer = useCustomerStore((state) => state);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedPaymentMethodType, setSelectedPaymentMethodType] = useState<
    string | "card"
  >(selectedProduct.paymentMethodTypes?.[0] || "card");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  
  // Get translations based on locale
  const locale = data.locale || 'en';
  const text = getTranslation(locale);

  useEffect(() => {
    if (elements && selectedProduct) {
      const updateData: any = {
        paymentMethodTypes: selectedProduct.paymentMethodTypes || ["card"],
        mode: "payment",
        amount: finalPrice,
        currency: selectedProduct.currency || "usd",
      };
      if (selectedProduct.paymentMethodTypes === "card") {
        updateData.setup_future_usage = "off_session";
      }
      elements.update(updateData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [elements, selectedProduct]);

  const getCurrencySymbol = (currency: string): string => {
    switch (currency) {
      case "eur":
        return "€";
      default:
        return "$";
    }
  };

  const handlePayment = async (event: React.FormEvent) => {
    setIsLoading(true);
    
    if (elements && selectedPaymentMethodType !== "affirm") {
      // Update the payment method type before submission
      let paymentMethodType = selectedPaymentMethodType;
      
      if (
        paymentMethodType === "google_pay" ||
        paymentMethodType === "apple_pay"
      ) {
        paymentMethodType = "card";
      }
      
      elements.update({
        mode: "payment",
        amount: finalPrice,
        currency: selectedProduct.currency || "usd",
        payment_method_types: [paymentMethodType],
        setup_future_usage: "off_session"
      });
    }

    // Use our service layer to handle payment submission
    const result = await handlePaymentSubmission({
      event,
      paymentIntentId,
      clientSecret,
      customer,
      price: finalPrice,
      termsAccepted,
      skuPriceMap,
      selectedProduct,
      selectedPaymentMethodType,
      currency: selectedProduct.currency
    });

    if (result.status === "error") {
      setErrorMessage(result.error || "An unknown error occurred");
      setIsLoading(false);
      return;
    }

    // Handle successful payment
    if (result.paymentIntent?.status === "succeeded" || result.paymentIntent?.status === "processing") {
      window.location.href =
        selectedProduct.confirmation_url +
        "?email=" +
        customer.email +
        "&payment_intent=" +
        result.paymentIntent.id +
        "&payment_intent_client_secret=" +
        result.paymentIntent.client_secret;
    }
  };

  return (
    <div className="container mx-auto p-4 w-full">
      <Suspense fallback={<ProductCardsSkeleton />}>
        <ProductDisplay
          productDisplayType={productDisplayType}
          products={products}
          selectedProductIndex={selectedProductIndex}
          currency={getCurrencySymbol(data.currency)}
          onProductChange={handleProductChange}
          theme={theme || "defaultTheme"}
        />
      </Suspense>
      
      {!bumpLast &&
        bumpProducts.length > 0 &&
        !selectedProduct.hideOrderBump && (
          <>
            <Suspense fallback={<div>Loading Headline...</div>}>
              <HeadlineBox headline={data.bumpHeadline} />
            </Suspense>
            <Suspense fallback={<div>Loading Offers...</div>}>
              <OrderBump
                bumpProducts={bumpProducts}
                onBumpChange={handleBumpChange}
                currency={getCurrencySymbol(data.currency)}
              />
            </Suspense>
          </>
        )}
        
      <div>
        <h3 className="mb-4 text-lg font-medium mt-4">{text.contactInformation}</h3>
        <LinkAuthenticationElement
          onChange={(event) => {
            const email = event.value.email;
            if (event.complete && email) {
              useCustomerStore.setState({
                email: email,
              });
              sendGTMEvent({
                event: "identify",
                email: email,
              });
            }
          }}
        />
      </div>
      
      <Suspense fallback={<ProductCardsSkeleton />}>
        <h3 className="mb-4 text-lg font-medium mt-4">{text.shippingInformation}</h3>
        <AddressForm />
      </Suspense>
      
      {bumpLast &&
        bumpProducts.length > 0 &&
        !selectedProduct.hideOrderBump && (
          <>
            {data.bumpHeadline ? (
              <Suspense fallback={<div>Loading Headline...</div>}>
                <HeadlineBox headline={data.bumpHeadline} />
              </Suspense>
            ) : null}
            <Suspense fallback={<div>Loading Offers...</div>}>
              <OrderBump
                bumpProducts={bumpProducts}
                onBumpChange={handleBumpChange}
                currency={getCurrencySymbol(data.currency)}
              />
            </Suspense>
          </>
        )}
        
      <>
        <h3 className="mb-4 text-lg font-medium mt-4">{text.paymentInformation}</h3>
        <PaymentElement
          onChange={(e) => {
            setSelectedPaymentMethodType(e.value.type);
          }}
        />
      </>
      
      <Suspense fallback={<ProductCardsSkeleton />}>
        <ProductSummary
          skuDetailsMap={skuDetailsMap}
          finalPrice={finalPrice}
          currency={getCurrencySymbol(data.currency)}
          locale={locale}
        />
      </Suspense>
      
      <TermsCheckbox
        termsUrl={
          data.tos_url ?? "https://www.tonyrobbins.com/terms-of-service"
        }
        termsAccepted={termsAccepted}
        setTermsAccepted={setTermsAccepted}
        ctaButtonText={data.ctaButtonText}
        locale={locale}
      />
      
      {errorMessage && (
        <div className="bg-red-500 text-white p-4 rounded mb-4">
          {errorMessage}
        </div>
      )}
      
      <button
        type="submit"
        className={` ${primaryButton} inline-flex items-center justify-center mt-4 rounded-full px-3.5 py-4 w-full font-semibold leading-6 shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-600 transition ease-in-out duration-150 ${
          isLoading ? "cursor-not-allowed" : ""
        }
        `}
        onClick={handlePayment}
        disabled={isLoading}
      >
        {isLoading ? (
          <>
            <svg
              className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            {text.processing}
          </>
        ) : (
          data.ctaButtonText ?? "Pay Now"
        )}
      </button>
    </div>
  );
};

export default PaymentForm;
